/**
 * How a person dresses themselves, as opposed to how an owner dresses the
 * venue (`theme.ts`). One colour, picked once, worn on every profile surface
 * that names them.
 *
 * The catalog is deliberately the same seven hues the felt already assigns by
 * seat index: a profile colour has to stay readable on dark baize and apart
 * from the felt's own reds and greens, and those hues are already tuned for
 * exactly that. At the table a pick becomes the seat's colour when it is
 * free; two players may choose the same hue, and the felt's whole job is
 * telling seats apart, so `resolveSeatHues` settles who keeps it.
 */

/** The profile colours, in the order the picker offers them. */
export const PROFILE_COLOR_IDS = [
  'sky',
  'amber',
  'violet',
  'magenta',
  'lime',
  'teal',
  'silver',
] as const;

export type ProfileColorId = (typeof PROFILE_COLOR_IDS)[number];

export interface ProfileColor {
  label: string;
  /** The hue itself — text, borders, the avatar disc. */
  hex: string;
  /** Its lit top, for the avatar's highlight. Precomputed, like chip faces. */
  highlight: string;
}

export const PROFILE_COLORS: Record<ProfileColorId, ProfileColor> = {
  sky: { label: 'Sky', hex: '#4fb8ff', highlight: '#9dd8ff' },
  amber: { label: 'Amber', hex: '#ffb347', highlight: '#ffd598' },
  violet: { label: 'Violet', hex: '#b18cff', highlight: '#d3bfff' },
  magenta: { label: 'Magenta', hex: '#f45fc0', highlight: '#f9a6dc' },
  lime: { label: 'Lime', hex: '#7ed957', highlight: '#b3e99b' },
  teal: { label: 'Teal', hex: '#40d6c8', highlight: '#8ce7de' },
  silver: { label: 'Silver', hex: '#ccd6dd', highlight: '#e3e9ed' },
};

export function isProfileColorId(value: unknown): value is ProfileColorId {
  return typeof value === 'string' && (PROFILE_COLOR_IDS as readonly string[]).includes(value);
}

/**
 * A stored colour, read leniently. Unlike a theme part, an unrecognised or
 * missing value resolves to `null` rather than a default: "never picked one"
 * is a real state the profile shows differently from any particular hue.
 */
export function readProfileColor(raw: unknown): ProfileColorId | null {
  return isProfileColorId(raw) ? raw : null;
}

/**
 * The hue to paint with, given a pick that may be absent. Callers that must
 * render *something* pass the fallback they already have — the seat colour on
 * the felt, or the neutral silver everywhere else.
 */
export function profileHex(
  color: ProfileColorId | null,
  fallback = PROFILE_COLORS.silver.hex,
): string {
  return color ? PROFILE_COLORS[color].hex : fallback;
}

/** Up to two letters for the avatar disc: initials, else the first letter. */
export function profileInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]!}${words[words.length - 1]![0]!}`.toUpperCase();
}

/** What the seat resolver needs to know about one player at the table. */
export interface SeatHueClaim {
  /** Seats in the order the player holds them; the first wears the pick. */
  seats: readonly number[];
  color: ProfileColorId | null;
}

/**
 * One hue per seat, the same on every screen, honouring picks where it can.
 *
 * The felt's job is telling seats apart, so no two seats may share a hue —
 * but a player who picked a colour should see it on their plate and their
 * chips. There are exactly as many hues as seats, so both hold:
 *
 * 1. In join order, each player's first seat takes their pick if nobody
 *    earlier already has it. First to sit keeps a contested hue.
 * 2. Every other seat — no pick, a lost contest, or a player's extra seat —
 *    takes its by-index hue if free, else the next free one round the wheel.
 *
 * Picks go first so a later arrival's choice is never lost to a seat that
 * never asked for a colour; the unpicked seat just moves to a free one.
 */
export function resolveSeatHues(
  players: readonly SeatHueClaim[],
): Record<number, ProfileColorId> {
  const n = PROFILE_COLOR_IDS.length;
  const hues: Record<number, ProfileColorId> = {};
  const taken = new Set<ProfileColorId>();
  const rest: number[] = [];

  for (const player of players) {
    player.seats.forEach((seat, i) => {
      if (i === 0 && player.color && !taken.has(player.color)) {
        hues[seat] = player.color;
        taken.add(player.color);
      } else {
        rest.push(seat);
      }
    });
  }

  for (const seat of [...rest].sort((a, b) => a - b)) {
    const start = ((seat % n) + n) % n;
    let pick: ProfileColorId = PROFILE_COLOR_IDS[start]!;
    for (let step = 0; step < n; step++) {
      const id = PROFILE_COLOR_IDS[(start + step) % n]!;
      if (!taken.has(id)) {
        pick = id;
        break;
      }
    }
    hues[seat] = pick;
    taken.add(pick);
  }
  return hues;
}
